import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, onCleanup, onMount, Show } from "solid-js";

import { createRoute } from "@tanstack/solid-router";
import { useNavigate } from "@/lib/u-router";
import { keepPreviousData } from "@tanstack/solid-query";
import { appRoute } from "./route";
import { Button } from "@/components/ui/button";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import {
  cn,
  getThemeGalleryQuery,
  getUserThemeSettingsQuery,
  queryKeys,
  updateBlurMutation,
  updateGlobalThemeMutation,
} from "@comandero/shared";
import type { ThemesRecord } from "@comandero/powersync";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { LogIn, Monitor, Moon, Palette, Sun } from "lucide-solid";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { PageContainer } from "@/components/ui/page-container";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { ThemeGalleryCard } from "./themes/theme-gallery-card";
import { type ColorMode, useTheme } from "../../providers/theme-provider";

// ─── Constants ────────────────────────────────────────────────────────────────

const INITIAL_LIMIT = 40;
const INCREMENT_LIMIT = 20;

// ─── Theme Gallery Page ────────────────────────────────────────────────────────

function ThemeGalleryPage() {
  const { t } = useI18n();
  const theme = useTheme();
  const navigate = useNavigate();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;
  const [themesLimit, setThemesLimit] = createSignal(INITIAL_LIMIT);
  const [sentinelRef, setSentinelRef] = createSignal<HTMLDivElement | null>(null);

  const themes = createPowerSyncQuery<ThemesRecord>({
    queryKey: () => queryKeys.themes.gallery(themesLimit()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getThemeGalleryQuery(dbInstance, themesLimit()) : null;
    },
    enabled: () => Boolean(drizzle()),
    placeholderData: keepPreviousData,
  });

  const userSettings = createPowerSyncQuery<{
    global_theme_id: string | null;
    is_blur: number | null;
    follow_device_theme: number | null;
  }>({
    queryKey: () => queryKeys.themes.userSettings(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = userId();
      return dbInstance && uid ? getUserThemeSettingsQuery(dbInstance, uid) : null;
    },
    enabled: () => Boolean(drizzle()) && Boolean(userId()),
    placeholderData: keepPreviousData,
  });

  // Intersection Observer para paginación infinita
  onMount(() => {
    const sentinel = sentinelRef();
    if (!sentinel) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          const current = themes.data;
          if (current && current.length >= themesLimit()) {
            setThemesLimit((l) => l + INCREMENT_LIMIT);
          }
        }
      },
      { threshold: 0.1, rootMargin: "400px" },
    );
    observer.observe(sentinel);
    onCleanup(() => {
      observer.disconnect();
    });
  });

  const currentGlobalThemeId = createMemo(() => userSettings.data?.[0]?.global_theme_id ?? null);

  const [optimisticBlur, setOptimisticBlur] = createSignal<boolean | null>(null);

  const currentBlur = createMemo(() => optimisticBlur() ?? userSettings.data?.[0]?.is_blur !== 0);

  const handleSelectTheme = async (themeId: string) => {
    const dbInstance = drizzle();
    const uid = userId();
    if (!dbInstance || !uid || themeId === currentGlobalThemeId()) {
      return;
    }
    try {
      // Tema y modo son ORTOGONALES: al elegir un tema NO tocamos el modo. El modo
      // actual (system/light/dark) sigue eligiendo la variante clara/oscura del tema.
      await updateGlobalThemeMutation(dbInstance, uid, themeId);
    } catch (err) {
      console.error("Error updating theme:", err);
    }
  };

  const handleResetToDefault = () => {
    theme.resetToDefaultTheme();
  };

  const handleBlurToggle = async () => {
    const dbInstance = drizzle();
    const uid = userId();
    if (!dbInstance || !uid) {
      return;
    }
    const newValue = !currentBlur();
    setOptimisticBlur(newValue);
    try {
      await updateBlurMutation(dbInstance, uid, newValue);
    } catch (err) {
      setOptimisticBlur(null);
      console.error("Error toggling blur:", err);
    }
  };

  // Apariencia canónica (sin login): el control de modo (system/light/dark) es
  // ORTOGONAL al tema activo. Cambiar de modo NO resetea el tema personalizado;
  // solo elige su variante clara/oscura. El botón "Default" del selector de temas
  // es el único que vuelve al tema por defecto.
  const appearanceOptions: { mode: ColorMode; label: MessageDescriptor; icon: typeof Monitor }[] = [
    { mode: "system", label: msg`System`, icon: Monitor },
    { mode: "light", label: msg`Light`, icon: Sun },
    { mode: "dark", label: msg`Dark`, icon: Moon },
  ];

  return (
    <div class="flex flex-1 flex-col overflow-hidden bg-transparent">
      <PageSubHeader title={t(msg`Theme Gallery`)} backTo="/profile" icon={Palette} />

      <PageContainer maxWidth="6xl" class="space-y-6">
        <div class="space-y-2">
          <h2 class="text-3xl font-extrabold tracking-tight">{t(msg`Choose your style`)}</h2>
          <p class="text-muted-foreground">
            {t(msg`Customize your experience. Themes sync across all your devices.`)}
          </p>
        </div>

        {/* ── Appearance (canónico — funciona sin login) ──────────── */}
        <div class="space-y-2">
          <p class="font-semibold text-sm">{t(msg`Appearance`)}</p>
          <div class="inline-flex rounded-lg border border-border bg-card/40 p-1">
            <For each={appearanceOptions}>
              {(opt) => {
                const active = () => theme.colorMode() === opt.mode;
                return (
                  <button
                    type="button"
                    class={cn(
                      "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                      active()
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    aria-pressed={active()}
                    onClick={() => theme.selectColorMode(opt.mode)}
                  >
                    <opt.icon class="h-4 w-4" />
                    <span>{t(opt.label)}</span>
                  </button>
                );
              }}
            </For>
          </div>
        </div>

        {/* ── Settings cards ──────────────────────────────────────── */}
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {/* Blur effect */}
          <Show when={currentGlobalThemeId()}>
            <Card>
              <CardContent class="flex items-center justify-between p-4">
                <div class="space-y-0.5">
                  <p class="font-semibold text-sm">{t(msg`Blur effect`)}</p>
                  <p class="text-xs text-muted-foreground">
                    {t(msg`Apply blur to background glow orbs`)}
                  </p>
                </div>
                <Switch
                  checked={currentBlur()}
                  onCheckedChange={() => {
                    void handleBlurToggle();
                  }}
                />
              </CardContent>
            </Card>
          </Show>

          {/* Grid pattern / lines */}
          <Card>
            <CardContent class="flex items-center justify-between p-4">
              <div class="space-y-0.5">
                <p class="font-semibold text-sm">{t(msg`Grid pattern`)}</p>
                <p class="text-xs text-muted-foreground">
                  {t(msg`Render grid line texture overlay`)}
                </p>
              </div>
              <Switch
                checked={theme.backgroundStyle() === "lines"}
                onCheckedChange={() => {
                  theme.toggleBackgroundStyle();
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* ── Custom themes (solo con login) ──────────────────────── */}
        <Show
          when={!session.isVisitor()}
          fallback={
            <Card>
              <CardContent class="flex flex-col items-center gap-3 p-8 text-center">
                <Palette class="h-8 w-8 text-muted-foreground" />
                <div class="space-y-1">
                  <p class="font-semibold">{t(msg`Unlock more themes`)}</p>
                  <p class="text-sm text-muted-foreground">
                    {t(
                      msg`Light and Dark are always available. Sign in to unlock the full theme gallery and sync it across your devices.`,
                    )}
                  </p>
                </div>
                <Button onClick={() => void navigate({ to: "/login" })}>
                  <LogIn class="mr-2 h-4 w-4" />
                  {t(msg`Sign in`)}
                </Button>
              </CardContent>
            </Card>
          }
        >
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {/* Volver al TEMA POR DEFECTO — ortogonal al modo (no cambia claro/oscuro). */}
            <button
              type="button"
              data-testid="theme-card-default"
              data-selected={!currentGlobalThemeId()}
              aria-pressed={!currentGlobalThemeId()}
              onClick={handleResetToDefault}
              class={cn(
                "group flex h-full min-h-40 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border bg-card p-6 text-center transition-all",
                currentGlobalThemeId()
                  ? "border-border hover:ring-2 hover:ring-primary/50"
                  : "border-primary ring-2 ring-primary shadow-lg shadow-primary/20",
              )}
            >
              <span
                class={cn(
                  "flex h-10 w-10 items-center justify-center rounded-full",
                  currentGlobalThemeId()
                    ? "bg-muted/20 text-muted-foreground"
                    : "bg-primary text-primary-foreground",
                )}
              >
                <Palette class="h-5 w-5" />
              </span>
              <span class="font-bold text-sm">{t(msg`Default`)}</span>
              <span class="text-xs text-muted-foreground">
                {t(msg`Comandero's built-in light & dark palette`)}
              </span>
            </button>
            <For each={themes.data}>
              {(themeItem) => (
                <ThemeGalleryCard
                  theme={themeItem}
                  isSelected={currentGlobalThemeId() === themeItem.id}
                  onSelect={handleSelectTheme}
                  t={t}
                />
              )}
            </For>
          </div>
          {/* Sentinel para carga infinita */}
          <div ref={setSentinelRef} class="h-10 w-full" aria-hidden="true" />
        </Show>
      </PageContainer>
    </div>
  );
}

export const themeGalleryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/themes",
  component: ThemeGalleryPage,
});
